import ReferencePathService from '~/services/referencePathServices/referencePathService'
import {IReferencePathNodeProcessFlowVariable, IReferencePathNodeSpecific, IReferencePathReferenceDetails, ReferencePathReferenceDetailsType} from '~/interfaces/referencePathInterfaces'
import ProcessFlowVariable from "~/models/processFlows/processFlowVariable";
import {GenericType} from '~/interfaces/genericTypeInterfaces'
import {ModelType} from '~/enums/modelType'
import {NumberType} from '~/enums/numberType'

export default class ProcessFlowVariablePathService extends ReferencePathService {
  get imposterModelType(): ModelType {
    return this.reference().variable_type
  }

  constructor(private variableNode: IReferencePathNodeProcessFlowVariable) {
    super();
  }

  get label(): string {
    return this.variableNode.reference()?.nameOrDefault;
  }

  get nodes(): IReferencePathNodeProcessFlowVariable[] {
    return [this.variableNode];
  }

  reference(): ProcessFlowVariable {
    return ProcessFlowVariable.repo.findById(this.variableNode.reference_id);
  }

  get referenceGenericModelType(): GenericType {
    return this.reference()?.genericType
  }

  get referenceModelType(): ModelType {
    return this.reference()?.modelType
  }

  canLinkTo(node: IReferencePathNodeSpecific): boolean {
    let decision: boolean
    const ref = this.reference()
    if (!ref) return false // guard against process flow variable variable missing
    if (ref.isVariableForm()) {
      decision = ref.reference_id === node.reference_id
    } else if (ref.isVariableFormElement()) {
      // since form element variable has a reference scope, the node right after the variable node is a form node
      // and the actual element node comes after
      // so must compare to the reference scope first here (the form will compare to the element in the next comparison (FormPathService comparing to FormElementPathService)
      decision = ref.reference_scope_id === node.reference_id
    } else if (ref.isVariableObjectModel()) {
      decision = ref.reference_id === node.reference_id
    } else if (ref.isVariableDocumentDynamicInput()) {
      // "Document Dynamic Inputs" essentially references a Certificate as a whole, since it's root is all of a Cert's dynamic inputs
      // therefore, the variable's reference_id still matches the Certificate node's reference_id rather than a particular CertificateDynamicInput's id
      decision = ref.reference_id === node.reference_id;
    } else if (ref.isVariableText()) {
      decision = !node // nothing can come after text variable
    } else if (ref.isVariableFile()) {
      decision = !node // nothing can come after file variable
    } else if (ref.isVariableNumber()) {
      decision = !node // nothing can come after number variable
    } else if (ref.isVariableBoolean()) {
      decision = !node // nothing can come after boolean variable
    } else if (ref.isVariableApiEndPoint()) {
      decision = ref.reference_scope_id === node.reference_id
    } else if (ref.isVariableDynamicInput()) {
      decision = ref.reference_scope_id === node.reference_id
    } else {
      decision = false
    }
    this.debugCanLinkTo('ProcessFlowVariablePathService', this.variableNode, node, decision)
    return decision
  }

  get referenceIsCollection(): boolean {
    return !this.reference().is_single
  }

  get referenceIsAppendable(): boolean {
    return this.referenceIsCollection || this.referenceIsText();
  }

  get referenceIsPdfCombinable(): boolean {
    return this.referenceIsFile() && this.referenceIsSingle
  }

  get referenceIsImageResizable(): boolean {
    return this.referenceIsFile()
  }

  get referenceDetails(): IReferencePathReferenceDetails {
    if (this.referenceGenericModelType === ModelType.Number) {
      return {
        type: ReferencePathReferenceDetailsType.Number,
        numType: NumberType.float,
        min: null,
        max: null,
      }
    } else if (this.referenceGenericModelType === ModelType.Boolean) {
      return {
        type: ReferencePathReferenceDetailsType.Boolean,
        options: {
          options: [
            {label: "True", value: true},
            {label: "False", value: false},
          ],
          selectedOption: null, // set elsewhere - possibly based on mapped variable source_constant boolean value
        },
      }
    } else {
      return {
        type: ReferencePathReferenceDetailsType.Text,
      }
    }
  }
}